import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUiState } from "@/lib/uiState";
import { Users, HeartPulse, Mail, MapPin, Plus, Upload, ArrowRight, ArrowUp, ArrowDown, Trophy, HandCoins, ShieldCheck } from "lucide-react";
import { useBranding } from "@/lib/branding";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function MiniBoard({ title, icon: Icon, rows, valueKey, format, unit, tint, testid }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  return (
    <div className="bg-card border border-border rounded-lg shadow-sm" data-testid={testid}>
      <div className="px-5 py-3 border-b border-border flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#C8A24C]" />
        <h2 className="font-serif text-lg tracking-tight">{title}</h2>
      </div>
      <div className="p-3 divide-y divide-border">
        {rows.map((r, i) => (
          <div key={r.class_year} className="flex items-center gap-3 px-2 py-2.5">
            <span className="w-7 text-center text-sm">{i < 3 ? ["🥇", "🥈", "🥉"][i] : `#${i + 1}`}</span>
            <span className="font-medium w-24 shrink-0 text-sm">Class of {r.class_year}</span>
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${Math.round(100 * (r[valueKey] || 0) / max)}%`, backgroundColor: tint }} />
            </div>
            <span className="text-sm font-semibold w-16 text-right">{format(r[valueKey] || 0)}{unit}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LeaderboardCard({ navigate }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/leaderboard").then((r) => setRows(r.data.rows)).catch(() => {}); }, []);
  if (rows.length === 0) return null;
  const byEngagement = [...rows].sort((a, b) => b.participation - a.participation).slice(0, 5);
  const byRaised = [...rows].sort((a, b) => b.raised - a.raised).slice(0, 5);
  return (
    <div className="mt-6" data-testid="dashboard-leaderboard">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif text-lg tracking-tight flex items-center gap-2"><Trophy className="h-4 w-4 text-[#C8A24C]" /> Top Classes</h2>
        <button data-testid="leaderboard-view-fundraising" onClick={() => navigate("/fundraising")} className="text-sm text-primary hover:underline flex items-center gap-1">Fundraising <ArrowRight className="h-3.5 w-3.5" /></button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MiniBoard title="Engagement" icon={HeartPulse} rows={byEngagement} valueKey="participation" format={(v) => v} unit="%" tint="#001A4B" testid="board-engagement" />
        <MiniBoard title="Giving" icon={HandCoins} rows={byRaised} valueKey="raised" format={money} unit="" tint="#C8A24C" testid="board-giving" />
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, onClick, testid, accent }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="text-left bg-card border border-border rounded-lg p-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex items-center gap-3"
  >
    <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${accent}`}>
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0">
      <div className="text-xl font-serif font-bold tracking-tight leading-none">{value?.toLocaleString() ?? "—"}</div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1 truncate">{label}</div>
    </div>
  </button>
);

export default function Dashboard() {
  const b = useBranding();
  const [stats, setStats] = useState(null);
  const { isAdmin, user } = useAuth();
  const [classSort, setClassSort] = useUiState("ud_dashboard_class_sort", "year");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  return (
    <div className="fade-up max-w-6xl mx-auto">
      <div className="relative overflow-hidden rounded-xl mb-6 border border-border shadow-sm">
        {b.hero_bg ? <img src={b.hero_bg} alt="Campus" className="absolute inset-0 h-full w-full object-cover" /> : null}
        <div className="absolute inset-0 bg-secondary/85" />
        <div className="relative z-10 px-6 sm:px-8 py-6 sm:py-7">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-accent">{b.university_name}{b.short_name ? ` · ${b.short_name}` : ""}</div>
          <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight mt-1.5">{b.tagline}</h1>
          <p className="text-white/75 text-sm mt-1.5 max-w-4xl lg:whitespace-nowrap">{b.login_subtext}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Overview</div>
          <h2 className="font-serif text-2xl tracking-tight mt-0.5">Dashboard</h2>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button data-testid="dashboard-add-alumnus" onClick={() => navigate("/alumni/new")} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">
              <Plus className="h-4 w-4" /> Add Alumnus
            </button>
            <button data-testid="dashboard-import" onClick={() => navigate("/import")} className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-border bg-card text-sm font-medium hover:bg-muted transition-colors">
              <Upload className="h-4 w-4" /> Import
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testid="stat-card-total-alumni" icon={Users} label="Total Alumni" value={stats?.total} accent="bg-primary/10 text-primary" onClick={() => navigate("/alumni")} />
        <StatCard testid="stat-card-living" icon={HeartPulse} label="Living Alumni" value={stats?.living} accent="bg-emerald-100 text-emerald-700" onClick={() => navigate("/alumni?living_status=living")} />
        <StatCard testid="stat-card-with-email" icon={Mail} label="With Valid Email" value={stats?.with_email} accent="bg-blue-100 text-blue-700" onClick={() => navigate("/alumni")} />
        <StatCard testid="stat-card-with-address" icon={MapPin} label="With Address" value={stats?.with_address} accent="bg-amber-100 text-amber-700" onClick={() => navigate("/alumni")} />
      </div>

      <div className="mt-6 bg-card border border-border rounded-lg shadow-sm">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-serif text-lg tracking-tight">Alumni by Class</h2>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1" data-testid="class-sort">
              {(() => {
                const isYear = classSort === "year" || classSort === "year_asc" || classSort === "year_desc";
                const yearAsc = classSort === "year_asc";
                return (
                  <button
                    data-testid="class-sort-year"
                    onClick={() => setClassSort(isYear ? (yearAsc ? "year_desc" : "year_asc") : "year_desc")}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${isYear ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}
                    title={isYear ? (yearAsc ? "Oldest first — click for newest first" : "Newest first — click for oldest first") : "Sort by year"}
                  >
                    Year {isYear && (yearAsc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                  </button>
                );
              })()}
              {[["most", "Most"], ["least", "Fewest"]].map(([k, label]) => (
                <button
                  key={k}
                  data-testid={`class-sort-${k}`}
                  onClick={() => setClassSort(k)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${classSort === k ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button data-testid="view-all-classes" onClick={() => navigate("/classes")} className="text-sm text-primary hover:underline flex items-center gap-1">
              All classes <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <div className="p-3">
          {!stats && <div className="text-muted-foreground text-sm p-2">Loading…</div>}
          {stats && stats.by_class.length === 0 && (
            <div className="text-muted-foreground text-sm p-4 text-center">No alumni yet. {isAdmin && "Import a spreadsheet or add records to get started."}</div>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5">
            {[...(stats?.by_class || [])].sort((a, b) => {
              if (classSort === "most") return b.count - a.count;
              if (classSort === "least") return a.count - b.count;
              const dir = classSort === "year_asc" ? 1 : -1;
              return dir * ((a.class_year ?? 0) - (b.class_year ?? 0));
            }).map((c) => {
              const max = Math.max(...stats.by_class.map((x) => x.count));
              return (
                <button
                  key={c.class_year}
                  data-testid={`class-chip-${c.class_year}`}
                  onClick={() => navigate(`/classes/${c.class_year}`)}
                  className="text-left border border-border rounded-md p-2.5 hover:border-primary hover:bg-muted transition-colors"
                >
                  <div className="flex items-center justify-between gap-1">
                    <div className="text-base font-semibold leading-none">{c.class_year}</div>
                    {c.has_agent && <span data-testid={`class-agent-pill-${c.class_year}`} className="inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-accent/20 text-primary" title="Class Agent assigned"><ShieldCheck className="h-2.5 w-2.5" /> Agent</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 mb-1.5">{c.count} alumni</div>
                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${(c.count / max) * 100}%` }} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {isAdmin && <LeaderboardCard navigate={navigate} />}
    </div>
  );
}
